// src/components/configuration/sections/team/TeamMemberListItem.jsx
import React from 'react';
import {
  Crown,
  Shield,
  User,
  Mail,
  Circle
} from 'lucide-react';
import { Card } from '../../../ui/card';
import { Badge } from '../../../ui/badge';
import { Avatar, AvatarFallback } from '../../../ui/avatar';
import { cn } from '../../../../utils/cn';

const TeamMemberListItem = ({
  member,
  type = 'member', // 'member' or 'invitation'
  isSelected = false,
  onClick,
  canManageInvitations = false
}) => {
  // Generate initials for avatar
  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Generate initials from email for invitations
  const getEmailInitials = (email) => {
    return email
      .split('@')[0]
      .slice(0, 2)
      .toUpperCase();
  };

  // Role configuration
  const getRoleConfig = (role) => {
    switch (role) {
      case 'admin':
        return {
          icon: Crown,
          label: 'Admin',
          className: 'bg-amber-500/20 text-amber-300 border-amber-500/30'
        };
      case 'manager':
        return {
          icon: Shield,
          label: 'Manager',
          className: 'bg-purple-500/20 text-purple-300 border-purple-500/30'
        };
      case 'staff':
        return {
          icon: User,
          label: 'Staff',
          className: 'bg-blue-500/20 text-blue-300 border-blue-500/30'
        };
      default:
        return {
          icon: User,
          label: 'Staff',
          className: 'bg-slate-500/20 text-slate-300 border-slate-500/30'
        };
    }
  };

  // Online status for members
  const getOnlineStatus = (lastSeen) => {
    if (!lastSeen) return { color: 'bg-slate-500' };

    const lastSeenDate = new Date(lastSeen);
    const now = new Date();
    const diffMinutes = (now - lastSeenDate) / (1000 * 60);

    if (diffMinutes < 5) return { color: 'bg-emerald-400' };
    else if (diffMinutes < 30) return { color: 'bg-amber-400' };
    else return { color: 'bg-slate-500' };
  };

  const roleConfig = getRoleConfig(member.role);
  const RoleIcon = roleConfig.icon;
  const onlineStatus = getOnlineStatus(member.user?.last_seen_at);

  // Render pending invitation
  if (type === 'invitation') {
    return (
      <Card
        onClick={onClick}
        className={cn(
          "relative cursor-pointer transition-all duration-200 group p-3",
          "hover:shadow-md hover:scale-[1.01]",
          "rounded-lg border bg-transparent backdrop-blur-none shadow-none",
          isSelected
            ? "bg-accent border-primary shadow-md"
            : "border-amber-500/40 bg-amber-500/10 hover:border-amber-400"
        )}
      >
        <div className="flex items-center gap-2 w-full">
          {/* Avatar - Email based */}
          <Avatar className={cn(
            "h-10 w-10 shrink-0",
            isSelected && "ring-2 ring-primary/40"
          )}>
            <AvatarFallback className="bg-muted border-2 border-dashed border-muted-foreground/50 text-muted-foreground text-xs">
              {getEmailInitials(member.email)}
            </AvatarFallback>
          </Avatar>

          {/* Info */}
          <div className="flex-1 min-w-0 overflow-hidden">
            <h3 className={cn(
              "font-semibold truncate text-sm transition-colors",
              isSelected ? "text-foreground" : "text-foreground/90"
            )}>
              {member.email}
            </h3>
            <div className="flex items-center gap-2 mt-1 overflow-hidden">
              <Badge variant="outline" className={cn("text-xs shrink-0", roleConfig.className)}>
                <RoleIcon className="w-3 h-3 mr-1" />
                {roleConfig.label}
              </Badge>
              <span className="text-xs text-amber-400 shrink-0">Pending</span>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  // Render team member
  return (
    <Card
      onClick={onClick}
      className={cn(
        "relative cursor-pointer transition-all duration-200 group p-3",
        "hover:shadow-md hover:scale-[1.01]",
        "rounded-lg border bg-transparent backdrop-blur-none shadow-none",
        isSelected
          ? "bg-accent border-primary shadow-md"
          : "bg-card border-border hover:border-muted-foreground",
        !member.is_active && "opacity-60"
      )}
    >
      <div className="flex items-center gap-2 w-full">
        {/* Avatar with online status */}
        <div className="relative shrink-0">
          <Avatar className={cn(
            "h-10 w-10",
            isSelected && "ring-2 ring-primary/40"
          )}>
            <AvatarFallback className={cn(
              "text-xs font-semibold",
              isSelected
                ? "bg-primary/30 text-primary-foreground"
                : "bg-muted text-muted-foreground"
            )}>
              {getInitials(member.user?.name)}
            </AvatarFallback>
          </Avatar>

          {/* Online Status Indicator */}
          {member.is_active && (
            <div className="absolute -bottom-0.5 -right-0.5">
              <div className={cn(
                "w-3 h-3 rounded-full border-2 border-background",
                onlineStatus.color
              )}>
                <Circle className="w-1.5 h-1.5 fill-current m-auto mt-[1px]" />
              </div>
            </div>
          )}
        </div>

        {/* Member Info */}
        <div className="flex-1 min-w-0 overflow-hidden">
          <h3 className={cn(
            "font-semibold truncate text-sm transition-colors",
            isSelected ? "text-foreground" : "text-foreground/90"
          )}>
            {member.user?.name}
          </h3>

          <div className="flex items-center gap-1.5 mt-1 overflow-hidden">
            <Badge variant="outline" className={cn("text-xs shrink-0", roleConfig.className)}>
              <RoleIcon className="w-3 h-3 mr-1" />
              {roleConfig.label}
            </Badge>

            {!member.is_active && (
              <Badge variant="secondary" className="text-xs bg-red-500/20 text-red-300 border-red-500/30 shrink-0">
                Inactive
              </Badge>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default TeamMemberListItem;
